import {
  PLATFORM_DEFAULT_LOCALE,
  localeMetadata,
  type LocaleMetadata,
  type PlatformLocaleTag,
} from "./locale-registry";
import { getLocale } from "./locale-state";
import { localeDateFormatter, localeListFormatter, preparedNumberFormatter } from "./intl-fallback";

const metadataFor = (locale: PlatformLocaleTag): LocaleMetadata =>
  localeMetadata(locale) ?? (localeMetadata(PLATFORM_DEFAULT_LOCALE) as LocaleMetadata);

/** BCP47 tag with the registry-owned calendar and numbering system. */
export const intlLocale = (locale: PlatformLocaleTag): string => {
  const metadata = metadataFor(locale);
  try {
    return new Intl.Locale(metadata.locale, {
      calendar: metadata.calendar,
      numberingSystem: metadata.numberingSystem,
    }).toString();
  } catch {
    return metadata.locale;
  }
};

/** The single native number-construction boundary, with the prepared MK provider. */
export const localeNumberFormatter = (tag: string, options?: Intl.NumberFormatOptions): Intl.NumberFormat =>
  preparedNumberFormatter(tag, options) ?? new Intl.NumberFormat(tag, options);

export const formatLocaleNumber = (
  value: number,
  locale: PlatformLocaleTag,
  options?: Intl.NumberFormatOptions,
): string => localeNumberFormatter(intlLocale(locale), options).format(value);

export const formatLocaleDate = (
  value: Date | number | string,
  locale: PlatformLocaleTag,
  options: Intl.DateTimeFormatOptions = {},
): string => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const metadata = metadataFor(locale);
  const resolved = metadata.defaultTimeZone && !options.timeZone
    ? { ...options, timeZone: metadata.defaultTimeZone }
    : options;
  return localeDateFormatter(intlLocale(locale), resolved).format(date);
};

export const formatLocaleRelativeTime = (
  value: number,
  unit: Intl.RelativeTimeFormatUnit,
  locale: PlatformLocaleTag,
  options: Intl.RelativeTimeFormatOptions = { numeric: "always" },
): string => {
  const tag = intlLocale(locale);
  // RelativeTimeFormat silently uses the runtime's default language when a
  // locale is unavailable. Callers must instead display the actual date.
  if (!Intl.RelativeTimeFormat.supportedLocalesOf([tag]).length) {
    throw new RangeError(`RelativeTimeFormat locale unavailable: ${locale}`);
  }
  const formatter = new Intl.RelativeTimeFormat(tag, options);
  if (new Intl.Locale(formatter.resolvedOptions().locale).language !== metadataFor(locale).baseLanguage) {
    throw new RangeError(`RelativeTimeFormat locale mismatch: ${locale}`);
  }
  return formatter.format(value, unit);
};

export const formatLocaleList = (
  values: readonly string[],
  locale: PlatformLocaleTag,
  options?: Intl.ListFormatOptions,
): string => localeListFormatter(intlLocale(locale), options).format(values);

export const localePluralCategory = (
  value: number,
  locale: PlatformLocaleTag,
  options?: Intl.PluralRulesOptions,
): Intl.LDMLPluralRule => new Intl.PluralRules(intlLocale(locale), options).select(value);

const collators = new Map<PlatformLocaleTag, Intl.Collator>();

/** Locale-aware display-text ordering; ordinal arguments avoid physical-direction assumptions. Never use for IDs, codes or ISO values. */
export const compareLocaleText = (
  first: string,
  second: string,
  locale: PlatformLocaleTag = getLocale(),
): number => {
  let collator = collators.get(locale);
  if (!collator) {
    collator = new Intl.Collator(intlLocale(locale), {
      usage: "sort",
      sensitivity: "base",
      numeric: true,
    });
    collators.set(locale, collator);
  }
  return collator.compare(first, second);
};

/** Locale-aware case folding for transient UI filtering; canonical text stays unchanged. */
export const foldLocaleText = (
  value: string,
  locale: PlatformLocaleTag = getLocale(),
): string => value.normalize("NFC").toLocaleLowerCase(intlLocale(locale));

/** Display-name casing only; never apply to identity seeds, addresses or protocol values. */
export const upperLocaleText = (
  value: string,
  locale: PlatformLocaleTag = getLocale(),
): string => value.normalize("NFC").toLocaleUpperCase(intlLocale(locale));

// Decimal zero code points for every numbering system in the approved registry.
// Canonical identifiers are never rewritten; call this only at numeric-input boundaries.
const DECIMAL_ZERO_CODE_POINTS = [
  0x0030, // latn
  0x0660, // arab
  0x06f0, // arabext
  0x0966, // deva
  0x09e6, // beng
  0x0a66, // guru
  0x0ae6, // gujr
  0x0be6, // tamldec
  0x0c66, // telu
  0x0ce6, // knda
  0x0d66, // mlym
  0x0de6, // sinh
  0x0e50, // thai
  0x0ed0, // laoo
  0x1040, // mymr
  0x17e0, // khmr
] as const;

export const normaliseDecimalDigits = (value: string): string =>
  [...value].map((character) => {
    const point = character.codePointAt(0);
    if (point === undefined) return character;
    for (const zero of DECIMAL_ZERO_CODE_POINTS) {
      if (point >= zero && point <= zero + 9) return String(point - zero);
    }
    return character;
  }).join("");

/** Security boundary: IDs/slugs/usernames remain byte-for-byte canonical. */
export const preserveCanonicalIdentifier = (value: string): string => value;
